import React, { useContext, useEffect, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import EventIcon from '@mui/icons-material/Event'
import CloseIcon from '@mui/icons-material/Close'
import API, { runGlobalApiErrorHandler } from '../../../../services/API'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import ConcurrentRoomLimitAlert from '../../../../components/Subscriptions/ConcurrentRoomLimitAlert'
import { isControlRoomLimit } from '../../../../helpers/controlRoomCapacity'

const StartControlRoomPopup = ({ event, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // The 403 payload when the plan's concurrent control-room capacity is in
  // use (shown inline instead of the generic error).
  const [limitInfo, setLimitInfo] = useState(null)
  const { setSelectedControlRoom, setSiteMode } = useContext(SiteModeContext)

  useEffect(() => {
    setLoading(false)
    setError(false)
    setLimitInfo(null)
  }, [event])

  const startControlRoom = () => {
    setLoading(true)
    setError(false)
    setLimitInfo(null)

    // skipGlobalErrorHandler so the concurrent-room refusal is explained
    // here; every other failure still goes to the global handler first, as
    // before.
    API.post(
      '/commandcontrol/dashboard/start-control-room',
      { event: event._id },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          setSelectedControlRoom(res.data)
          setSiteMode('eventControl')
        } else if (isControlRoomLimit(res)) {
          setLimitInfo(res.data)
          setError(true)
        } else if (!runGlobalApiErrorHandler({ response: res })) {
          setError(true)
        }
      })
      .catch((e) => {
        if (!runGlobalApiErrorHandler(e)) setError(true)
      })
      .finally(() => setLoading(false))
  }

  return (
    <BigModal
      open={!!event}
      onClose={onClose}
      autoWidth
      style={{
        padding: 0,
        width: { xs: '94vw', sm: 'auto' },
        maxWidth: { xs: '94vw', sm: '90vw' },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          overflow: 'hidden',
          // borderRadius: 4,
        }}
      >
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 3,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" padding={2}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
                boxShadow: '0 12px 30px rgba(0,0,0,0.22)',
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 30 }} />
            </Box>

            <Stack spacing={0.25}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Command & Control
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                Launch Control Room
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                Create an operational control room for this event.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Stack alignItems="center" spacing={2} padding={3}>
            <CircularProgress sx={{ color: '#239AA4' }} />
            <Typography fontWeight={800} color="#242F40">
              Creating control room...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Preparing the operational environment for this event.
            </Typography>
          </Stack>
        ) : error ? (
          <Stack spacing={2.5} padding={3}>
            {limitInfo ? (
              <ConcurrentRoomLimitAlert limitInfo={limitInfo} />
            ) : (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                Sorry, we're unable to start the control room at this time.
                Please contact AmbuNet support.
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                onClick={onClose}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 3,
                  color: '#64748B',
                }}
              >
                Close
              </Button>

              <Button
                onClick={startControlRoom}
                variant="contained"
                endIcon={<RocketLaunchIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  px: 3,
                  backgroundColor: '#239AA4',
                  '&:hover': { backgroundColor: '#1E7F88' },
                }}
              >
                Try Again
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2.5} padding={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#E6F7F9',
                    color: '#1E7F88',
                    flexShrink: 0,
                  }}
                >
                  <EventIcon />
                </Box>

                <Stack spacing={0.25}>
                  <Typography fontWeight={900} color="#242F40">
                    {event?.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This event does not currently have a control room.
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
                border: '1px solid rgba(35,154,164,0.24)',
              }}
            >
              A control room will allow authorised users to manage incidents,
              logs, decisions and operational updates for this event.
            </Alert>

            <Divider />

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                onClick={onClose}
                startIcon={<CloseIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 3,
                  color: '#64748B',
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={startControlRoom}
                variant="contained"
                endIcon={<RocketLaunchIcon />}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  px: 3,
                  backgroundColor: '#239AA4',
                  boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
                  '&:hover': {
                    backgroundColor: '#1E7F88',
                    boxShadow: '0 16px 34px rgba(35,154,164,0.34)',
                  },
                }}
              >
                Create & Launch
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </BigModal>
  )
}

export default StartControlRoomPopup
